/**
 * The machinery behind a "tabs across time" listing.
 *
 * The publications bar was the first of these: for every period the block's
 * query could cover, ask the catalogue how many results it has in that
 * period, and give a tab to the ones that have any. Nothing in that idea is
 * about publications, or even about years - courses run in semesters, and
 * `/teaching/WS1213` is to a semester what `/publications/2019` is to a year.
 *
 * So the parts that differ per content type live in a *spec*:
 *
 *   periods(querystring, vocabulary)  the candidates, newest first
 *   narrow(querystring, periods)      that query, restricted to them
 *   of(item)                          an item's period, for grouping
 *   label(period)                     what the tab says
 *   segment(period) / period(segment) the folder a period has, if any
 *   param                             the URL parameter for an in-place pick
 *
 * Optional: `scope(querystring, base)` for counting on a period page,
 * `withoutPeriod(querystring)` for listing on one, `vocabulary` naming the
 * candidates when `periods` finds none in the query, `navLabel` and `noun`.
 *
 * Everything below is the same for all of them.
 */

/** The block's query without its own paging. */
export const queryAsIs = (querystring) => {
  const { limit, offset, b_size, ...rest } = querystring || {};
  return rest;
};

/**
 * The query rescoped to the folder that holds the period pages.
 *
 * On a period page the block's own query is restricted to that page's folder,
 * which would leave the bar with a single tab; counting against the base
 * instead gives it the same bar the overview page has.
 */
export const scopeToBase = (querystring, base) => {
  const rest = { ...querystring };
  if (!base) return rest;
  const scoped = {
    i: 'path',
    o: 'plone.app.querystring.operation.string.path',
    v: base,
    depth: -1,
  };
  const criteria = rest.query || [];
  return {
    ...rest,
    query: criteria.some((c) => c.i === 'path')
      ? criteria.map((c) => (c.i === 'path' ? scoped : c))
      : [...criteria, scoped],
  };
};

/** The values of a vocabulary already in the store, as strings. */
export const vocabularyValues = (state, name) =>
  state?.vocabularies?.[name]?.items?.map((t) => String(t.label ?? t.value));

/** Trailing slashes off, so paths can be compared. */
export const normalizePath = (path) => (path || '').replace(/\/+$/, '');

/** A count only: one item is enough to learn `items_total`. */
export const countQuery = (spec, querystring, period) => ({
  ...spec.narrow(queryAsIs(querystring), [period]),
  b_size: 1,
  fullobjects: 0,
});

export const countSubrequest = (key, period) => `${key}-count-${period}`;

/**
 * Where one selection's results are kept.
 *
 * The selection is part of the key on purpose. The counts arrive one response
 * at a time, so the set of periods worth showing can change while they land,
 * and each change starts another request. Sharing one slot meant whichever
 * response happened to arrive last won - which is not the same as the one
 * that is current. With a slot per selection a late reply lands in its own
 * and is simply never read.
 */
export const itemsSubrequest = (key, selection) =>
  `${key}-items-${selection || 'all'}`;

/** Which periods the page should show, read from the URL. */
export const selectedPeriods = (spec, search, available, defaultCount) => {
  const wanted = new URLSearchParams(search || '').get(spec.param);
  if (wanted && available.includes(wanted)) {
    return [wanted];
  }
  const count = Number(defaultCount) > 0 ? Number(defaultCount) : 2;
  return available.slice(0, count);
};

/** The period this page is the page *of*, if it is one. */
export const periodOfPage = (spec, base, pathname) => {
  if (!base || !spec.period) return null;
  const here = normalizePath(pathname);
  if (!here.startsWith(`${base}/`)) return null;
  const rest = here.slice(base.length + 1);
  return rest.includes('/') ? null : spec.period(rest);
};

/** Where a period's own page lives. */
export const periodHref = (spec, base, period) =>
  spec.segment ? `${base}/${spec.segment(period)}` : null;

/**
 * A key for this block's own subrequests.
 *
 * Listing variations are handed the block's data but not its id, so the id
 * cannot be used here. The query itself identifies the block just as well and
 * is available on both sides - the server loader and the component compute
 * the same key from it, which is what lets the server-rendered results be
 * picked up in the browser instead of being fetched a second time.
 */
export const querystringKey = (querystring, base, name) => {
  const stable = JSON.stringify([
    (querystring?.query || []).map((c) => [c.i, c.o, c.v]),
    querystring?.sort_on,
    querystring?.sort_order,
    querystring?.depth,
    base || '',
    name || '',
  ]);
  let hash = 5381;
  for (let i = 0; i < stable.length; i += 1) {
    hash = ((hash << 5) + hash + stable.charCodeAt(i)) | 0;
  }
  return `ptabs${(hash >>> 0).toString(36)}`;
};

/** One period of results, generous enough that no period here is cut off. */
export const ITEMS_PER_PERIOD = 300;
